/**
 * API Bearer bridge — the ONLY path browser app code uses to reach app data.
 *
 * Per ADR-0006, the browser never reads/writes app tables through Supabase. It
 * calls the Worker `/api/*` routes and, for protected routes, attaches the
 * Supabase session access token as `Authorization: Bearer <token>`.
 *
 * Rules enforced here:
 * - The token is sent ONLY in the Authorization header, never in the URL/query.
 * - This bridge is the ONLY place Authorization is set, and only from the explicit
 *   token argument; any caller-supplied Authorization header is dropped.
 * - No token is logged.
 * - When no token is provided, no Authorization header is sent (the caller may
 *   still hit public routes such as /api/healthz).
 */

// Same base-URL behavior the app already uses. Empty string => same-origin /api/*.
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

export type FetchLike = typeof fetch;

export interface ApiRequestOptions {
  /** Supabase session access token; attached as `Authorization: Bearer <token>`. */
  token?: string | null;
  method?: string;
  /** JSON-serializable body; when present, Content-Type: application/json is set. */
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/** Build the absolute request URL. Path only — never carries the token. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

/**
 * Attach `Authorization: Bearer <token>` only when a non-empty token is given.
 * Any caller-supplied Authorization/authorization header in `base` is dropped so
 * the explicit token argument is the single source of the Authorization header.
 * A valid token is used verbatim (never trimmed/modified).
 */
export function buildAuthHeaders(
  token?: string | null,
  base: Record<string, string> = {},
): Record<string, string> {
  const { Authorization: _authorization, authorization: _lowerAuthorization, ...safeBase } = base;
  const headers: Record<string, string> = { ...safeBase };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

/**
 * Perform an API request. `fetchImpl` is injectable so tests can assert the
 * request without a live network. The token is placed in the Authorization
 * header only; it is never appended to the URL.
 */
export async function apiFetch(
  path: string,
  options: ApiRequestOptions = {},
  fetchImpl: FetchLike = fetch,
): Promise<Response> {
  const { token, method = 'GET', body, signal, headers: extraHeaders } = options;
  const headers = buildAuthHeaders(token, extraHeaders);

  const init: RequestInit = { method, headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  if (signal) {
    init.signal = signal;
  }

  return fetchImpl(apiUrl(path), init);
}

/**
 * Whether the caller is an administrator.
 *
 * `not-admin` and `error` are distinguished only so the interface can say
 * something truthful: both deny access. `GET /api/me/admin` (A1) is an
 * assertion rather than a status report — 200 means administrator, 403 means
 * not — so a non-administrator learns exactly one bit and the body carries no
 * identity, role, or membership detail.
 */
export type AdminStatusResult =
  | { state: 'admin' }
  | { state: 'not-admin' }
  | { state: 'error'; status: number };

/**
 * Ask the server whether this caller is an administrator.
 *
 * The browser cannot answer this for itself. `admin_users` and
 * `admin_memberships` have RLS enabled and are revoked from `anon` and
 * `authenticated` with no policies, so a caller-JWT client cannot read them at
 * all — not even its own row. This endpoint is the only way a browser can learn
 * its own administrator status, which is what makes a client-side bypass
 * structurally impossible rather than merely blocked.
 *
 * **Fails closed.** Anything that is not a 200 denies. A network failure, a
 * gateway error, and an explicit 403 all leave the caller without access; only
 * an affirmative answer grants it.
 */
export async function fetchAdminStatus(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminStatusResult> {
  try {
    const res = await apiFetch('/api/me/admin', { token }, fetchImpl);
    if (res.ok) return { state: 'admin' };
    // 403 is the designed refusal; 401 means the token is not usable here.
    if (res.status === 403 || res.status === 401) return { state: 'not-admin' };
    return { state: 'error', status: res.status };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * Launch-health counts (A4b-1). Aggregates only — the endpoint returns no row,
 * identifier, email, or amount, and this type says so.
 */
export interface AdminStatsPayload {
  purchases: { pending: number; completed: number; failed: number; expired: number; refunded: number };
  entitlements: { total: number };
  stripeEvents: { unprocessed: number };
  assessments: { bottleneck: number; work_fit: number; perspective: number };
  results: { total: number };
}

export type AdminStatsResult =
  | { state: 'ok'; stats: AdminStatsPayload }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/**
 * Read the launch-health counts.
 *
 * `forbidden` is kept distinct from `error` so the console can say something
 * truthful, but neither yields numbers. A failure is never rendered as zeros:
 * a dashboard reporting no unprocessed Stripe events because it could not reach
 * the endpoint would hide the exact failure it exists to reveal.
 */
export async function fetchAdminStats(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminStatsResult> {
  try {
    const res = await apiFetch('/api/admin/stats', { token }, fetchImpl);
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { stats: AdminStatsPayload };
    return { state: 'ok', stats: body.stats };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * Production's Key Metrics (Admin migration §2, spec §4).
 *
 * Seven numbers, which is the whole payload. Formatting is the console's job,
 * as it is in production: `$X.XX`, `+N in last 7 days`, `N of M purchased`.
 */
export interface AdminMetricsPayload {
  totalUsers: number;
  recentUsers: number;
  totalAssessments: number;
  revenueCents: number;
  payingCustomers: number;
  totalPurchases: number;
  conversionRate: number;
  /** The caption's N: `Based on N Bottleneck assessments.` */
  bottleneckAssessments: number;
  averageScores: {
    bottleneck: number;
    executionReadiness: number;
    recoveryDeficit: number;
    clarity: number;
  };
  /** The three chart datasets, each already ordered `count DESC`. */
  typeBreakdown: AdminBreakdown[];
  bottleneckBreakdown: AdminBreakdown[];
  workFitTypeBreakdown: AdminBreakdown[];
}

/** One bar. `label` is what production renders; `count` is its height. */
export interface AdminBreakdown {
  label: string;
  count: number;
}

export type AdminMetricsResult =
  | { state: 'ok'; metrics: AdminMetricsPayload }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/**
 * Read the Key Metrics.
 *
 * A failure is never rendered as zeros. Production shows `Error loading admin
 * data` when its own load fails, and reproducing that means the console has to
 * be able to tell "nothing sold" from "we could not read".
 */
export async function fetchAdminMetrics(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminMetricsResult> {
  try {
    const res = await apiFetch('/api/admin/metrics', { token }, fetchImpl);
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { metrics: AdminMetricsPayload };
    return { state: 'ok', metrics: body.metrics };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * One row of the Admin's Users table (spec §7). The complete allowlist the
 * endpoint returns — this type is the whole of what the browser is permitted to
 * know about a customer from the console.
 */
export interface AdminUserRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  revenueCents: number;
  discountUsed: boolean;
  isPayingCustomer: boolean;
  lastLoginAt: string | null;
  createdAt: string | null;
  supportAccessGrantedAt: string | null;
  latestType: string | null;
}

export type AdminUsersResult =
  | { state: 'ok'; users: AdminUserRow[] }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/**
 * Read the active user list.
 *
 * Production takes no search, sort, filter, or page parameter, so neither does
 * this: the whole active directory is the page, and finding a customer is
 * `Ctrl+F`. A failure is never an empty list — a table missing the customer an
 * operator is looking for reads as "no such account".
 */
export async function fetchAdminUsers(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminUsersResult> {
  try {
    const res = await apiFetch('/api/admin/users', { token }, fetchImpl);
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { users: AdminUserRow[] };
    return { state: 'ok', users: body.users };
  } catch {
    return { state: 'error', status: 0 };
  }
}

export type AdminCsvResult =
  | { state: 'ok'; filename: string; body: string }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/** The four export controls production's action bar carries (spec §3). */
export const ADMIN_EXPORTS = [
  { label: 'Export Bottleneck Answers', path: '/api/admin/exports/bottleneck-answers.csv' },
  { label: 'Export Work Fit Answers', path: '/api/admin/exports/work-fit-answers.csv' },
  { label: 'Export Perspective Answers', path: '/api/admin/exports/perspective-answers.csv' },
  { label: 'Export Emails', path: '/api/admin/users/export' },
] as const;

/**
 * Fetch one export file. The filename comes from `Content-Disposition`, so
 * production owns it — including the mismatches it carries.
 */
export async function fetchAdminExport(
  token: string,
  path: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminCsvResult> {
  try {
    const res = await apiFetch(path, { token }, fetchImpl);
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    return {
      state: 'ok',
      filename: filenameFrom(res.headers.get('content-disposition')),
      body: await res.text(),
    };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * Fetch production's **Export Emails** file (spec §13).
 *
 * **Fetched rather than linked.** Production's control is a plain link, because
 * its session travels in a cookie; V2 authenticates with a bearer token, which
 * a link cannot carry. The endpoint, the filename, and the file are identical —
 * only the way the browser is handed them differs, and that difference is
 * forced by the auth model rather than chosen.
 *
 * The filename comes from `Content-Disposition`, so production owns it: the
 * control says "Export Emails" and the file is `users.csv`.
 */
export async function fetchAdminUsersCsv(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminCsvResult> {
  try {
    const res = await apiFetch('/api/admin/users/export', { token }, fetchImpl);
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    return {
      state: 'ok',
      filename: filenameFrom(res.headers.get('content-disposition')),
      body: await res.text(),
    };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/** `attachment; filename="users.csv"` → `users.csv`. */
function filenameFrom(header: string | null): string {
  const match = header?.match(/filename="([^"]+)"/);
  return match?.[1] ?? 'users.csv';
}

/**
 * The console's five operator actions (spec section 8, section 11).
 *
 * Each resolves to `true` only when the write landed. A refusal and a failure
 * both resolve to `false`, because the console says the same thing about both:
 * an operator needs to know the action did not happen, and the reason is the
 * server's business.
 */
async function mutate(
  path: string,
  method: 'POST' | 'DELETE' | 'PATCH',
  token: string,
  fetchImpl: FetchLike,
  body?: unknown,
): Promise<Response | null> {
  try {
    // `apiFetch` serialises the body and sets the content type itself; handing
    // it a string would encode the JSON twice.
    return await apiFetch(
      path,
      body === undefined ? { token, method } : { token, method, body },
      fetchImpl,
    );
  } catch {
    return null;
  }
}

/** Unlock Reports. Resolves to how many results were granted, or `null`. */
export async function grantAdminReports(
  token: string,
  userId: string,
  fetchImpl: FetchLike = fetch,
): Promise<number | null> {
  const res = await mutate(
    `/api/admin/users/${encodeURIComponent(userId)}/grant-reports`,
    'POST',
    token,
    fetchImpl,
  );
  if (!res || !res.ok) return null;
  const body = (await res.json().catch(() => null)) as { grantedCount?: number } | null;
  return typeof body?.grantedCount === 'number' ? body.grantedCount : 0;
}

/** Archive. A soft delete; the customer's own data is untouched. */
export async function archiveAdminUser(
  token: string,
  userId: string,
  fetchImpl: FetchLike = fetch,
): Promise<boolean> {
  const res = await mutate(`/api/admin/users/${encodeURIComponent(userId)}`, 'DELETE', token, fetchImpl);
  return res?.ok === true;
}

/** Restore. */
export async function restoreAdminUser(
  token: string,
  userId: string,
  fetchImpl: FetchLike = fetch,
): Promise<boolean> {
  const res = await mutate(
    `/api/admin/users/${encodeURIComponent(userId)}/restore`,
    'POST',
    token,
    fetchImpl,
  );
  return res?.ok === true;
}

/** Delete assessment. Irreversible. */
export async function deleteAdminAssessment(
  token: string,
  resultId: string,
  fetchImpl: FetchLike = fetch,
): Promise<boolean> {
  const res = await mutate(
    `/api/admin/assessments/${encodeURIComponent(resultId)}`,
    'DELETE',
    token,
    fetchImpl,
  );
  return res?.ok === true;
}

/** Approve or un-approve a review. */
export async function setAdminReviewStatus(
  token: string,
  reviewId: string,
  status: string,
  fetchImpl: FetchLike = fetch,
): Promise<boolean> {
  const res = await mutate(
    `/api/admin/reviews/${encodeURIComponent(reviewId)}`,
    'PATCH',
    token,
    fetchImpl,
    { status },
  );
  return res?.ok === true;
}

/**
 * One review awaiting moderation (spec §11). Production's field set exactly.
 *
 * The console renders only the count today: production holds zero reviews and
 * its populated row layout has never been observed. The type carries the whole
 * contract so the slice that eventually renders it needs no second endpoint.
 */
export interface AdminReviewRow {
  id: string;
  userId: string | null;
  purchaseId: string | null;
  rating: number | null;
  body: string;
  status: string;
  displayName: string | null;
  consentToPublish: boolean;
  approvedAt: string | null;
  createdAt: string | null;
  userEmail: string | null;
  productKey: string | null;
}

export type AdminReviewsResult =
  | { state: 'ok'; reviews: AdminReviewRow[] }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/**
 * Read the moderation list. A failure is never an empty queue: "nothing is
 * waiting" is a decision an operator acts on by doing nothing.
 */
export async function fetchAdminReviews(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminReviewsResult> {
  try {
    const res = await apiFetch('/api/admin/reviews', { token }, fetchImpl);
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { reviews: AdminReviewRow[] };
    return { state: 'ok', reviews: body.reviews };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * The report status badge (spec §9). `null` means production's derivation
 * established nothing and the row carries no badge.
 */
export type AdminReportStatus = 'free' | 'paid' | 'coupon' | 'credit' | 'unlocked';

/** One assessment inside an expanded user row (spec §9). */
export interface AdminAssessmentRow {
  id: string;
  primaryType: string | null;
  assessmentType: string;
  completedAt: string | null;
  /** Position in this customer's whole history, oldest first, all types. */
  sequence: number;
  reportStatus: AdminReportStatus | null;

  /**
   * The purchase detail block on the right of the row. All six are null when
   * the assessment was never paid for, and no block is drawn. Amounts are
   * cents; the row divides.
   */
  purchaseListAmount: number | null;
  purchaseAmountPaid: number | null;
  purchaseDiscountAmount: number | null;
  purchaseCouponCode: string | null;
  purchasePaymentStatus: string | null;
  purchaseStripeSessionId: string | null;
}

export type AdminAssessmentsResult =
  | { state: 'ok'; assessments: AdminAssessmentRow[] }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/**
 * Read one customer's assessment history, for an expanded row.
 *
 * Fetched when a row is opened rather than with the page, which is how
 * production loads it: its page load is four calls and this is not one of them.
 */
export async function fetchAdminUserAssessments(
  token: string,
  userId: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminAssessmentsResult> {
  try {
    const res = await apiFetch(
      `/api/admin/users/${encodeURIComponent(userId)}/assessments`,
      { token },
      fetchImpl,
    );
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { assessments: AdminAssessmentRow[] };
    return { state: 'ok', assessments: body.assessments };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * One row of the **Archived Users** table (spec §10). A smaller allowlist than
 * the active row on purpose: production's archived table shows Name, Email,
 * Revenue and Archived, and nothing else.
 */
export interface AdminArchivedUserRow {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  revenueCents: number;
  archivedAt: string | null;
}

export type AdminArchivedUsersResult =
  | { state: 'ok'; users: AdminArchivedUserRow[] }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/**
 * Read the archived list. A failure is never an empty archive: an operator
 * reading "nobody is archived" would conclude an archive had been undone.
 */
export async function fetchAdminArchivedUsers(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminArchivedUsersResult> {
  try {
    const res = await apiFetch('/api/admin/deleted-users', { token }, fetchImpl);
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { users: AdminArchivedUserRow[] };
    return { state: 'ok', users: body.users };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * Customer search (S4). The seven-field allowlist the endpoint returns, and
 * nothing more — this type is the whole of what the browser is permitted to
 * know about a person from a search.
 */
export interface AdminCustomerPayload {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  createdAt: string | null;
  lastSignInAt: string | null;
  emailConfirmedAt: string | null;
}

export interface AdminCustomerSearchPayload {
  customers: AdminCustomerPayload[];
  search: {
    query: string;
    status: 'empty' | 'too-short' | 'ok';
    minQueryLength: number;
    limit: number;
    capped: boolean;
    scanComplete: boolean;
  };
}

export type AdminCustomerSearchResult =
  | { state: 'ok'; payload: AdminCustomerSearchPayload }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/**
 * Search customers by email.
 *
 * **A failure is never an empty list.** The endpoint answers "does this
 * customer exist?", so a search that could not run must not look like a
 * confident "no" — an agent would tell a paying customer they have no account.
 * `error` and `forbidden` are kept distinct from a genuine zero-result search,
 * and the interface renders them differently.
 */
export async function fetchAdminCustomers(
  token: string,
  query: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminCustomerSearchResult> {
  try {
    const res = await apiFetch(
      `/api/admin/customers?q=${encodeURIComponent(query)}`,
      { token },
      fetchImpl,
    );
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const payload = (await res.json()) as AdminCustomerSearchPayload;
    return { state: 'ok', payload };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * The Operations Center's work list (V1). Mirrors the Incident Engine's output
 * exactly; the browser adds no derivation of its own, because the contract makes
 * the engine the single source of truth for what an incident says.
 */
export type IncidentReadiness = 'yes' | 'not-here' | 'not-yet';

export interface IncidentEvidence {
  kind: 'purchase' | 'attempt';
  purchaseId?: string;
  attemptId?: string;
  completedAt?: string;
  assessmentType?: string | null;
  amountTotalCents?: number | null;
  currency?: string | null;
  processedPaymentEventIds?: string[];
  unprocessedPaymentEventIds?: string[];
}

export interface AffectedCustomer {
  customerId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  reason: string;
  since: string;
  waitingMs: number;
  evidence: IncidentEvidence;
}

export interface UnattributedPayment {
  eventId: string;
  receivedAt: string;
  waitingMs: number;
}

export interface Incident {
  id: string;
  type: string;
  signature: string;
  severity: 'critical' | 'warning';
  impact: string;
  cause: string;
  recommendation: string;
  readiness: IncidentReadiness;
  expectedOutcome: string | null;
  affected: AffectedCustomer[];
  unattributedPayments: UnattributedPayment[];
}

export type AdminIncidentsResult =
  | { state: 'ok'; incidents: Incident[] }
  | { state: 'forbidden' }
  | { state: 'error'; status: number };

/**
 * Read the open incidents.
 *
 * **A failure is never an empty list.** Zero incidents renders as "nothing needs
 * your attention", so an unreachable endpoint presented as zero would tell an
 * administrator the platform is healthy at the moment it cannot see.
 */
export async function fetchAdminIncidents(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<AdminIncidentsResult> {
  try {
    const res = await apiFetch('/api/admin/incidents', { token }, fetchImpl);
    if (res.status === 403 || res.status === 401) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { incidents: Incident[] };
    return { state: 'ok', incidents: body.incidents };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/** Shape returned by GET /api/me/profile (matches the Worker route). */
export interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
}

/**
 * Auth-bound smoke call to the protected profile route. A 404 means "profile not
 * created yet" — a normal signed-in state, NOT a fatal app error.
 */
export type ProfileResult =
  | { state: 'ok'; profile: Profile }
  | { state: 'not-created' }
  | { state: 'error'; status: number };

/**
 * Name the caller. Production asks for both on the Welcome screen and refuses
 * when either is blank; the server applies the same rule.
 */
export async function updateMyProfileName(
  token: string,
  names: { firstName: string; lastName: string },
  fetchImpl: FetchLike = fetch,
): Promise<{ state: 'ok'; profile: Profile } | { state: 'invalid' } | { state: 'error' }> {
  try {
    const res = await apiFetch(
      '/api/me/profile',
      // `apiFetch` serialises the body and sets the content type itself.
      { token, method: 'PATCH', body: names },
      fetchImpl,
    );
    if (res.status === 400) return { state: 'invalid' };
    if (!res.ok) return { state: 'error' };
    const body = (await res.json()) as { profile: Profile };
    return { state: 'ok', profile: body.profile };
  } catch {
    return { state: 'error' };
  }
}

export async function fetchMyProfile(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<ProfileResult> {
  const res = await apiFetch('/api/me/profile', { token }, fetchImpl);

  if (res.status === 404) {
    return { state: 'not-created' };
  }
  if (!res.ok) {
    return { state: 'error', status: res.status };
  }

  const body = (await res.json()) as { profile: Profile };
  return { state: 'ok', profile: body.profile };
}

export type AssessmentType = 'bottleneck' | 'work_fit' | 'perspective';

export interface AssessmentAttempt {
  id: string;
  assessment_type: AssessmentType;
  attempt_number: number;
  status: string;
  created_at: string;
  completed_at: string | null;
}

export type StartAttemptResult =
  | { state: 'ok'; attempt: AssessmentAttempt }
  | { state: 'limit-reached' }
  | { state: 'error'; status: number };

export type ListAttemptsResult =
  | { state: 'ok'; attempts: AssessmentAttempt[] }
  | { state: 'error'; status: number };

export async function startAssessmentAttempt(
  token: string,
  type: AssessmentType,
  fetchImpl: FetchLike = fetch,
): Promise<StartAttemptResult> {
  const res = await apiFetch(`/api/assessments/${type}/attempts`, { token, method: 'POST' }, fetchImpl);

  if (res.status === 409) {
    return { state: 'limit-reached' };
  }

  if (!res.ok) {
    return { state: 'error', status: res.status };
  }

  const body = (await res.json()) as { attempt: AssessmentAttempt };
  return { state: 'ok', attempt: body.attempt };
}

export async function listAssessmentAttempts(
  token: string,
  type?: AssessmentType,
  fetchImpl: FetchLike = fetch,
): Promise<ListAttemptsResult> {
  const path = type
    ? `/api/assessments/attempts?type=${encodeURIComponent(type)}`
    : '/api/assessments/attempts';

  const res = await apiFetch(path, { token }, fetchImpl);

  if (!res.ok) {
    return { state: 'error', status: res.status };
  }

  const body = (await res.json()) as { attempts: AssessmentAttempt[] };
  return { state: 'ok', attempts: body.attempts };
}

/**
 * Every assessment the caller has completed (production's `/assessments/me`).
 *
 * The dashboard's Reports tab is built on this: it lists locked assessments as
 * well as unlocked ones, which `/api/reports` cannot serve because it returns
 * unlocked reports only.
 */
export type MyAssessmentsResult =
  | { state: 'ok'; assessments: FreeResult[] }
  | { state: 'error'; status: number };

export async function listMyAssessments(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<MyAssessmentsResult> {
  try {
    const res = await apiFetch('/api/assessments/me', { token }, fetchImpl);
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { assessments: FreeResult[] };
    return { state: 'ok', assessments: body.assessments };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * One daily check-in (production's `/api/checkins`).
 */
export interface CheckinRow {
  id: string;
  energyLevel: number;
  focusLevel: number;
  note: string | null;
  createdAt: string;
}

export type CheckinsResult =
  | { state: 'ok'; checkins: CheckinRow[] }
  | { state: 'error'; status: number };

/** The caller's check-in history, newest first. */
export async function listCheckins(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<CheckinsResult> {
  try {
    const res = await apiFetch('/api/checkins', { token }, fetchImpl);
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { checkins: CheckinRow[] };
    return { state: 'ok', checkins: body.checkins };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/** Save one check-in. Resolves to the saved row, or `null` when it did not save. */
export async function createCheckin(
  token: string,
  input: { energyLevel: number; focusLevel: number; note: string },
  fetchImpl: FetchLike = fetch,
): Promise<CheckinRow | null> {
  try {
    const res = await apiFetch('/api/checkins', { token, method: 'POST', body: input }, fetchImpl);
    if (!res.ok) return null;
    const body = (await res.json()) as { checkin: CheckinRow };
    return body.checkin;
  } catch {
    return null;
  }
}

/**
 * The customer's own Support Access toggle (production's
 * `PATCH /me/support-access`).
 */
export type SupportAccessResult =
  | { state: 'ok'; supportAccessGrantedAt: string | null }
  | { state: 'error'; status: number };

export async function setSupportAccess(
  token: string,
  granted: boolean,
  fetchImpl: FetchLike = fetch,
): Promise<SupportAccessResult> {
  try {
    const res = await apiFetch(
      '/api/me/support-access',
      { token, method: 'PATCH', body: { granted } },
      fetchImpl,
    );
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { supportAccessGrantedAt: string | null };
    return { state: 'ok', supportAccessGrantedAt: body.supportAccessGrantedAt };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * The customer's own Delete Account (production's `DELETE /me`).
 *
 * A soft delete, as production's own copy says: it points at support "to
 * request permanent erasure". The browser signs out afterwards.
 */
export async function deleteMyAccount(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<boolean> {
  try {
    const res = await apiFetch('/api/me', { token, method: 'DELETE' }, fetchImpl);
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Whether the caller has granted the support team read access, and when.
 *
 * Production carries this on its `users/me` payload. V2 has no such endpoint,
 * so the Settings tab reads it here.
 */
export type MyAccountResult =
  | { state: 'ok'; supportAccessGrantedAt: string | null }
  | { state: 'error'; status: number };

export async function fetchMyAccount(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<MyAccountResult> {
  try {
    const res = await apiFetch('/api/me/account', { token }, fetchImpl);
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { supportAccessGrantedAt: string | null };
    return { state: 'ok', supportAccessGrantedAt: body.supportAccessGrantedAt };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/** One of the caller's completed purchases (production's `/purchases/me`). */
export interface MyPurchase {
  id: string;
  resultId: string | null;
  productKey: string | null;
  assessmentType: string | null;
  createdAt: string;
}

export type MyPurchasesResult =
  | { state: 'ok'; purchases: MyPurchase[] }
  | { state: 'error'; status: number };

export async function listMyPurchases(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<MyPurchasesResult> {
  try {
    const res = await apiFetch('/api/purchases/me', { token }, fetchImpl);
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { purchases: MyPurchase[] };
    return { state: 'ok', purchases: body.purchases };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/** One of the caller's own reviews (production's `/reviews/me`). */
export interface MyReview {
  id: string;
  purchaseId: string | null;
  rating: number | null;
  body: string;
  status: string;
  displayName: string | null;
  consentToPublish: boolean;
  createdAt: string | null;
}

export type MyReviewsResult =
  | { state: 'ok'; reviews: MyReview[] }
  | { state: 'error'; status: number };

export async function listMyReviews(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<MyReviewsResult> {
  try {
    const res = await apiFetch('/api/reviews/me', { token }, fetchImpl);
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { reviews: MyReview[] };
    return { state: 'ok', reviews: body.reviews };
  } catch {
    return { state: 'error', status: 0 };
  }
}

/**
 * The refusals the server names, in production's wording.
 *
 * Production shows whatever the server said in a toast; V2 says the same thing
 * in the form, and each refusal is a sentence a customer can act on rather than
 * a code.
 */
const REVIEW_REFUSALS: Record<string, string> = {
  already_reviewed: 'You have already reviewed this report.',
  purchase_not_found: 'This report has not been purchased.',
  not_owner: 'That report belongs to another account.',
  result_not_found: 'That report could not be found.',
  invalid_rating: 'Please select a rating before submitting.',
};

export type CreateReviewResult =
  | { state: 'ok'; review: MyReview }
  | { state: 'error'; message: string };

/** Write a review. The status is never sent: every review starts pending. */
export async function createReview(
  token: string,
  input: {
    resultId: string;
    rating: number;
    body: string;
    displayName?: string | undefined;
    consentToPublish: boolean;
  },
  fetchImpl: FetchLike = fetch,
): Promise<CreateReviewResult> {
  try {
    const res = await apiFetch('/api/reviews', { token, method: 'POST', body: input }, fetchImpl);
    if (res.ok) {
      const body = (await res.json()) as { review: MyReview };
      return { state: 'ok', review: body.review };
    }

    const failure = (await res.json().catch(() => null)) as { error?: string } | null;
    return {
      state: 'error',
      message: REVIEW_REFUSALS[failure?.error ?? ''] ?? 'Error submitting review',
    };
  } catch {
    return { state: 'error', message: 'Error submitting review' };
  }
}

// ── Free-assessment journey (V2-014) ────────────────────────────────────────────

/** One question as delivered to the browser (public; never carries reverseScored). */
export interface QuestionItem {
  id: string;
  prompt: string;
  order: number;
}

export type QuestionsResult =
  | { state: 'ok'; assessmentType: AssessmentType; questions: QuestionItem[] }
  | { state: 'error'; status: number };

/** Public question delivery — no token required (a signed-out user can render/answer). */
export async function fetchAssessmentQuestions(
  type: AssessmentType,
  fetchImpl: FetchLike = fetch,
): Promise<QuestionsResult> {
  const res = await apiFetch(`/api/assessments/${type}/questions`, {}, fetchImpl);
  if (!res.ok) {
    return { state: 'error', status: res.status };
  }
  const body = (await res.json()) as { assessmentType: AssessmentType; questions: QuestionItem[] };
  return { state: 'ok', assessmentType: body.assessmentType, questions: body.questions };
}

export interface SubmittedAnswer {
  questionId: string;
  value: number;
}

export type SubmitAssessmentResult =
  | { state: 'ok'; attemptId: string; resultId: string }
  | { state: 'limit-reached' }
  | { state: 'invalid'; errors: unknown[] }
  | { state: 'error'; status: number };

/**
 * Authenticated submission. The submissionId is the client idempotency key — the
 * same id is sent for retries and the anonymous claim, so repeated calls never
 * create a second attempt/result. 200 (idempotent replay) and 201 (created) are both
 * successes and resolve to the same identifiers.
 */
export async function submitAssessment(
  token: string,
  type: AssessmentType,
  submissionId: string,
  answers: SubmittedAnswer[],
  fetchImpl: FetchLike = fetch,
): Promise<SubmitAssessmentResult> {
  const res = await apiFetch(
    `/api/assessments/${type}/submit`,
    { token, method: 'POST', body: { submissionId, answers } },
    fetchImpl,
  );

  if (res.status === 409) {
    return { state: 'limit-reached' };
  }
  if (res.status === 422) {
    const body = (await res.json()) as { errors?: unknown[] };
    return { state: 'invalid', errors: body.errors ?? [] };
  }
  if (!res.ok) {
    return { state: 'error', status: res.status };
  }
  const body = (await res.json()) as { attemptId: string; resultId: string };
  return { state: 'ok', attemptId: body.attemptId, resultId: body.resultId };
}

/** Free-result payload (server-computed; presentation scores recomputed at read). */
export interface FreeResult {
  id: string;
  attemptId: string;
  assessmentType: AssessmentType;
  typeSlug: string;
  primaryType: string;
  shortExplanation: string;
  categoryScores: Record<string, number>;
  topCategories: string[];
  isUnlocked: boolean;
  completedAt: string | null;
  bottleneckScore?: number;
  executionReadinessScore?: number;
  recoveryDeficitScore?: number;
  clarityScore?: number;
  presentOperatorScore?: number;
  isBlended?: boolean;
  secondaryPatternKey?: string | null;
}

export type FetchResultResult =
  | { state: 'ok'; result: FreeResult }
  | { state: 'not-found' }
  | { state: 'error'; status: number };

export async function fetchFreeResult(
  token: string,
  id: string,
  fetchImpl: FetchLike = fetch,
): Promise<FetchResultResult> {
  const res = await apiFetch(`/api/assessments/results/${id}`, { token }, fetchImpl);
  if (res.status === 404) {
    return { state: 'not-found' };
  }
  if (!res.ok) {
    return { state: 'error', status: res.status };
  }
  const body = (await res.json()) as { result: FreeResult };
  return { state: 'ok', result: body.result };
}

// ── Paid-report journey (V2-015) ────────────────────────────────────────────────

/** One day of a bottleneck/perspective plan (server-owned paid content). */
export interface DayPlan {
  day: number;
  focus: string;
  objective: string;
  coachingNote: string;
  actions: string[];
  journalPrompts: string[];
}

/** One week of a 30-day plan (server-owned paid content). */
export interface WeekPlan {
  week: number;
  theme: string;
  goals: string[];
  behaviors: string[];
  journalPrompts: string[];
}

export interface BottleneckReportContent {
  fullExplanation: string;
  whyThisDevelops: string;
  hiddenCost: string;
  falseBelief: string;
  sabotagePattern: string;
  whatYouNeedMost: string;
  truthToHear: string;
  whatChanges: string;
  whatThisMeans: string;
  stopDoing: string[];
  startDoing: string[];
  sevenDayPlan: DayPlan[];
  thirtyDayPlan: WeekPlan[];
  reflectionPrompts: string[];
  recommendedHabits: string[];
  warningSignals: string[];
  nextBestStep: string;
}

export interface WorkFitReportContent {
  profileDeepDive: {
    whatThisProfileNeeds: string;
    whatDrainsThisProfile: string;
    howThisProfileEvaluatesRoles: string;
    whatThisProfileMayOverlook: string;
  };
  managerFitGuide: {
    supportiveStyle: string;
    frictionStyle: string;
    questionsToAsk: string[];
    warningSigns: string[];
  };
  cultureRiskGuide: {
    cultureRisks: string[];
    whatToListenFor: string[];
    languageThatHidesPoorFit: string[];
    howToTellValuesAreReal: string;
  };
  interviewQuestions: Array<{ category: string; question: string }>;
  offerChecklist: Array<{ category: string; item: string }>;
  currentRoleReflection: {
    frictionMayBeEnvironmental: string[];
    whatToClarifyWithManager: string[];
    whatToAskForFirst: string[];
    signsEnvironmentWontChange: string[];
  };
  successConditions: { thirtyDay: string[]; sixtyDay: string[]; ninetyDay: string[] };
  finalDecisionGuide: {
    greenLights: string[];
    yellowFlags: string[];
    redFlags: string[];
    decisionPrompts: string[];
  };
}

export interface WFDimensionDeepDive {
  dimension: string;
  label: string;
  whatThisMeans: string;
  goodFitLooksLike: string;
  poorFitLooksLike: string;
  questionsToAsk: string[];
  redFlagsToWatchFor: string[];
}

export interface WFMisfitSignal {
  dimension: string;
  label: string;
  poorFitLooksLike: string;
  redFlagsToWatchFor: string[];
}

export interface PerspectiveReportContent {
  perspectiveSnapshot: string;
  patternStrengths: string[];
  costWhenOverused: string[];
  selfTalkPattern: string;
  timeOrientationPattern: string;
  beliefFriction: string;
  focusControlPattern: string;
  immediateReframe: string;
  abcdeExample: {
    activatingEvent: string;
    belief: string;
    consequence: string;
    disputation: string;
    newEffect: string;
  };
  dashboardSummary: string;
}

export interface InterpretationTrap {
  questionId: string;
  label: string;
  description: string;
  active: boolean;
}

/** Discriminated report payload — mirrors the API serializeReport output. */
export type ReportPayload =
  | { assessmentType: 'bottleneck'; content: BottleneckReportContent }
  | {
      assessmentType: 'work_fit';
      content: WorkFitReportContent;
      environmentNeeds: WFDimensionDeepDive[];
      misfitSignals: WFMisfitSignal[];
    }
  | {
      assessmentType: 'perspective';
      content: PerspectiveReportContent;
      sevenDayReset: DayPlan[];
      thirtyDayPractice: WeekPlan[];
      disclaimer: string;
      interpretationTraps: InterpretationTrap[];
    };

export interface FullReportResponse {
  result: FreeResult;
  report: ReportPayload;
  /**
   * The caller's previous result of this type, when there is one. Production
   * opens "Since Your Last Assessment / What Changed" from it; a first-time
   * taker has none and sees no section.
   */
  previousResult?: FreeResult | null;
}

export type StartCheckoutResult =
  | { state: 'ok'; url: string }
  | { state: 'already-unlocked' }
  | { state: 'error'; status: number };

/** Create (or reuse) a Checkout Session for a result and get the Stripe-hosted URL. */
export async function startCheckout(
  token: string,
  resultId: string,
  fetchImpl: FetchLike = fetch,
): Promise<StartCheckoutResult> {
  const res = await apiFetch('/api/purchases/checkout', { token, method: 'POST', body: { resultId } }, fetchImpl);
  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (body.error === 'already_unlocked') return { state: 'already-unlocked' };
    return { state: 'error', status: 409 };
  }
  if (!res.ok) {
    return { state: 'error', status: res.status };
  }
  const body = (await res.json()) as { url: string };
  return { state: 'ok', url: body.url };
}

export type ConfirmCheckoutResult =
  | { state: 'ok'; unlocked: boolean; status: string | null; resultId: string | null }
  | { state: 'error'; status: number };

/** Authenticated confirmation of a returned Checkout Session (webhook fallback). */
export async function confirmCheckout(
  token: string,
  sessionId: string,
  fetchImpl: FetchLike = fetch,
): Promise<ConfirmCheckoutResult> {
  const res = await apiFetch('/api/purchases/confirm', { token, method: 'POST', body: { sessionId } }, fetchImpl);
  if (!res.ok) {
    return { state: 'error', status: res.status };
  }
  const body = (await res.json()) as { unlocked: boolean; status: string | null; resultId: string | null };
  return { state: 'ok', unlocked: body.unlocked, status: body.status, resultId: body.resultId };
}

export type FetchFullReportResult =
  | { state: 'ok'; data: FullReportResponse }
  | { state: 'locked'; assessmentType?: string }
  | { state: 'not-found' }
  | { state: 'error'; status: number };

/** Fetch the entitlement-gated full report (402 → locked, 404 → not found). */
export async function fetchFullReport(
  token: string,
  id: string,
  fetchImpl: FetchLike = fetch,
): Promise<FetchFullReportResult> {
  const res = await apiFetch(`/api/reports/${id}/full`, { token }, fetchImpl);
  if (res.status === 402) {
      // The refusal names the assessment so the page can word itself.
      const body = (await res.json().catch(() => null)) as { assessmentType?: string } | null;
      return { state: 'locked', ...(body?.assessmentType ? { assessmentType: body.assessmentType } : {}) };
    }
  if (res.status === 404) return { state: 'not-found' };
  if (!res.ok) return { state: 'error', status: res.status };
  const body = (await res.json()) as FullReportResponse;
  return { state: 'ok', data: body };
}

export interface ReportLibraryItem {
  resultId: string;
  assessmentType: AssessmentType;
  typeSlug: string;
  primaryType: string;
  completedAt: string | null;
}

export type FetchReportLibraryResult =
  | { state: 'ok'; reports: ReportLibraryItem[] }
  | { state: 'error'; status: number };

/** The caller's unlocked reports for durable return access. */
export async function fetchReportLibrary(
  token: string,
  fetchImpl: FetchLike = fetch,
): Promise<FetchReportLibraryResult> {
  const res = await apiFetch('/api/reports', { token }, fetchImpl);
  if (!res.ok) return { state: 'error', status: res.status };
  const body = (await res.json()) as { reports: ReportLibraryItem[] };
  return { state: 'ok', reports: body.reports };
}

// ── Offer-impression instrumentation (V2-017) ─────────────────────────────────────

export type RecordOfferImpressionResult = { state: 'ok' } | { state: 'error' };

/**
 * Record a single offer_impression for a locked paid-report offer the authenticated
 * owner has actually seen. This is the only client-produced conversion event.
 *
 * The request body is EXACTLY `{ resultId }`: the user id is derived server-side from
 * the verified bearer token and is never sent, and no answers, scores, type, email,
 * route, browser data, metadata, or generic event payload is attached. Both 201
 * (first insert) and 200 (idempotent replay) are success. This is fire-and-observe
 * instrumentation — it never throws, so a failure cannot block the report UI, and
 * the database UNIQUE(result_id) remains the final idempotency boundary.
 */
export async function recordOfferImpression(
  token: string,
  resultId: string,
  fetchImpl: FetchLike = fetch,
): Promise<RecordOfferImpressionResult> {
  try {
    const res = await apiFetch(
      '/api/offer/impression',
      { token, method: 'POST', body: { resultId } },
      fetchImpl,
    );
    return res.ok ? { state: 'ok' } : { state: 'error' };
  } catch {
    return { state: 'error' };
  }
}
